
import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import { useSearchParams } from "react-router-dom";

import {
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Briefcase,
  Eye,
  X,
} from "lucide-react";

import {
  fetchJobList,
  fetchJobListFilters,
  fetchJobMedia,
} from "../services/jobListService";

import {
  getMediaDiscrepancy,
  updateMediaDiscrepancy,
} from "../services/mediaDiscrepancyService";

import "./JobList.css";

const PAGE_SIZE = 20;

function JobList() {
  const storedUser =
    localStorage.getItem("user") ||
    localStorage.getItem("localUser");

  let currentUser = {};
  try {
    currentUser = storedUser ? JSON.parse(storedUser) : {};
  } catch (error) {
    currentUser = {};
  }

  const isInternalAdmin =
    currentUser.organization_type?.toUpperCase() === "INTERNAL" &&
    currentUser.org_role?.toUpperCase() === "ADMIN";

  const [searchParams] = useSearchParams();

  // Parse URL Parameters
  const campaignIdFromUrl = searchParams.get("campaign_id") || "";
  const statusFromUrl = searchParams.get("status") || "";
  const discrepancyFromUrl = searchParams.get("discrepancy") || "";

  /*
  ========================================================
  JOB DATA
  ========================================================
  */

  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  /*
  ========================================================
  FILTER & SEARCH DATA
  ========================================================
  */

  const [campaigns, setCampaigns] = useState([]);
  const [brands, setBrands] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const [filters, setFilters] = useState({
    campaign:
      !isInternalAdmin && campaignIdFromUrl === "other"
        ? ""
        : campaignIdFromUrl,
    brand: "",
    status: statusFromUrl,
    discrepancy: discrepancyFromUrl,
  });

  const [jobSearch, setJobSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  /*
  ========================================================
  LOADING / ERROR
  ========================================================
  */

  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [error, setError] = useState("");

  /*
  ========================================================
  MEDIA MODAL & DISCREPANCY STATE
  ========================================================
  */

  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaImageLoading, setMediaImageLoading] = useState(false);
  const [mediaError, setMediaError] = useState("");
  const [mediaJob, setMediaJob] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);

  const [mediaDiscrepancy, setMediaDiscrepancy] = useState(null);
  const [mediaDiscrepancyLoading, setMediaDiscrepancyLoading] = useState(false);
  const [mediaDiscrepancySaving, setMediaDiscrepancySaving] = useState(false);

  const [mediaDiscrepancies, setMediaDiscrepancies] = useState({});
  const [mediaRemark, setMediaRemark] = useState("");
  const [mediaRemarkSaving, setMediaRemarkSaving] = useState(false);
  const [showMediaRemark, setShowMediaRemark] = useState(false);

  /*
  ========================================================
  DEBOUNCE SEARCH EFFECT
  ========================================================
  */

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(jobSearch.trim());
      setPage(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [jobSearch]);

  /*
  ========================================================
  URL PARAMETER SYNC & DROPDOWN MATCH EFFECT
  ========================================================
  */

  useEffect(() => {
    const matchedStatus =
      statuses.find(
        (status) =>
          String(status.value).toLowerCase() ===
          String(statusFromUrl).toLowerCase()
      )?.value || statusFromUrl;

    setFilters((previous) => ({
      ...previous,
      campaign: campaignIdFromUrl,
      status: matchedStatus,
      discrepancy: discrepancyFromUrl,
    }));

    setPage(1);
  }, [campaignIdFromUrl, statusFromUrl, discrepancyFromUrl, statuses]);

  /*
  ========================================================
  LOAD FILTERS
  ========================================================
  */

  const loadFilters = useCallback(async () => {
    try {
      setFiltersLoading(true);

      const response = await fetchJobListFilters();

      if (response?.success) {
        setCampaigns(response.campaigns || []);
        setBrands(response.brands || []);
        setStatuses(response.statuses || []);
      }
    } catch (err) {
      console.error("[JOB LIST] Filter API error:", err);
    } finally {
      setFiltersLoading(false);
    }
  }, []);

  /*
  ========================================================
  LOAD JOBS
  ========================================================
  */

  const loadJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetchJobList({
        page,
        pageSize: PAGE_SIZE,
        campaignId: filters.campaign,
        brandId: filters.brand,
        status: filters.status,
        discrepancy: filters.discrepancy,
        search: appliedSearch.trim(),
      });

      const data = response?.data || {};

      setJobs(data.items || []);
      setTotal(Number(data.total || 0));
      setTotalPages(Number(data.total_pages || 0));
    } catch (err) {
      console.error("[JOB LIST] Job API error:", err);

      setJobs([]);
      setTotal(0);
      setTotalPages(0);

      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to load jobs."
      );
    } finally {
      setLoading(false);
    }
  }, [
    page,
    filters.campaign,
    filters.brand,
    filters.status,
    filters.discrepancy,
    appliedSearch,
  ]);

  /*
  ========================================================
  INITIAL LOAD & DEPENDENCIES
  ========================================================
  */

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  /*
  ========================================================
  FILTER HANDLERS
  ========================================================
  */

  const handleFilterChange = (filterName, value) => {
    setFilters((previous) => ({
      ...previous,
      [filterName]: value,
    }));

    setPage(1);
  };

  const handleReset = () => {
    setFilters({
      campaign: "",
      brand: "",
      status: "",
      discrepancy: "",
    });

    setJobSearch("");
    setAppliedSearch("");
    setPage(1);
  };

  const handleRefresh = () => {
    loadFilters();
    loadJobs();
  };

  /*
  ========================================================
  VIEW JOB
  ========================================================
  */

  const handleViewJob = (job) => {
    const requestId =
      job?.request_id || job?.job_id || job?.id;

    if (!requestId) {
      console.error("[JOB LIST] Missing request_id/job_id:", job);
      return;
    }

    window.open(
      `/job-detail-test/${requestId}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  /*
  ========================================================
  MEDIA HELPERS & HANDLERS
  ========================================================
  */

  const getMediaUrl = (media) => {
    if (!media) return null;

    if (typeof media === "string") {
      return media;
    }

    if (media?.url && typeof media.url === "object") {
      return media.url.url || media.url.src || null;
    }

    return (
      media.url ||
      media.fileUrl ||
      media.image_url ||
      media.imageUrl ||
      media.src ||
      media.path ||
      null
    );
  };

  const checkIsVideo = (media) => {
    if (!media) return false;
    const mediaUrl = getMediaUrl(media);

    return (
      media?.type === "video" ||
      media?.media_type === "video" ||
      media?.mime_type?.startsWith("video/") ||
      /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(mediaUrl || "")
    );
  };

  const getMediaKey = (media, index = 0) => {
    if (!media) return `media-${index}`;

    const url = getMediaUrl(media);
    const sourceType = media.source_type || media.sourceType || "media";
    const sourceId = media.source_id ?? media.sourceId ?? "unknown";
    const mediaIndex = media.media_index ?? media.mediaIndex ?? index;

    return `${sourceType}-${sourceId}-${mediaIndex}-${url || ""}`;
  };

  const loadMediaDiscrepancy = useCallback(
    async (media, index = selectedMediaIndex) => {
      if (!media) {
        setMediaDiscrepancy(null);
        return;
      }

      const url = getMediaUrl(media);

      if (!url) {
        setMediaDiscrepancy(null);
        return;
      }

      const sourceType = media.source_type || media.sourceType;
      const sourceId = media.source_id || media.sourceId;

      if (!sourceType || !sourceId) {
        console.warn("[MEDIA DISCREPANCY] Missing source metadata:", media);
        setMediaDiscrepancy(null);
        return;
      }

      const jobId =
        mediaJob?.job_id || mediaJob?.request_id || mediaJob?.id;

      if (!jobId) {
        console.warn("[MEDIA DISCREPANCY] Missing job ID");
        return;
      }

      const mediaKey = getMediaKey(media, index);

      try {
        setMediaDiscrepancyLoading(true);

        const mediaIndex =
          media?.media_index ?? media?.mediaIndex ?? index;

        const result = await getMediaDiscrepancy({
          jobId,
          sourceType,
          sourceId,
          mediaIndex,
          mediaUrl: url,
        });

        const discrepancyValue =
          result?.discrepancy === undefined
            ? null
            : result.discrepancy === null
            ? null
            : Boolean(result.discrepancy);

        const remarkValue =
          result?.discrepancy_reason ??
          result?.discrepancyReason ??
          "";

        setMediaDiscrepancies((previous) => ({
          ...previous,
          [mediaKey]: {
            discrepancy: discrepancyValue,
            remark: remarkValue,
          },
        }));

        setMediaDiscrepancy(discrepancyValue);
        setMediaRemark(remarkValue);
        setShowMediaRemark(discrepancyValue === true);
      } catch (error) {
        console.error(
          "[MEDIA DISCREPANCY] Failed to load discrepancy:",
          error
        );

        setMediaDiscrepancy(null);
        setMediaRemark("");
      } finally {
        setMediaDiscrepancyLoading(false);
      }
    },
    [mediaJob, selectedMediaIndex]
  );

  /*
  ========================================================
  DISCREPANCY SELECTION EFFECT
  ========================================================
  */

  useEffect(() => {
    if (!mediaOpen || mediaItems.length === 0) {
      return;
    }

    const selectedMedia = mediaItems[selectedMediaIndex];

    loadMediaDiscrepancy(selectedMedia, selectedMediaIndex);
  }, [mediaOpen, selectedMediaIndex, mediaItems, loadMediaDiscrepancy]);

  const handleDiscrepancyChange = async (value) => {
    const media = mediaItems[selectedMediaIndex];

    if (!media) return;

    const url = getMediaUrl(media);

    const jobId =
      mediaJob?.job_id || mediaJob?.request_id || mediaJob?.id;

    const sourceType = media.source_type || media.sourceType;
    const sourceId = media.source_id || media.sourceId;

    if (!jobId || !url || !sourceType || !sourceId) {
      console.error(
        "[MEDIA DISCREPANCY] Missing required payload data:",
        { jobId, url, sourceType, sourceId }
      );

      alert("Cannot save discrepancy: Missing source metadata.");
      return;
    }

    const mediaKey = getMediaKey(media, selectedMediaIndex);

    const mediaIndex =
      media?.media_index ?? media?.mediaIndex ?? selectedMediaIndex;

    if (value === false) {
      try {
        setMediaDiscrepancySaving(true);

        await updateMediaDiscrepancy({
          jobId,
          sourceType,
          sourceId,
          mediaIndex,
          mediaUrl: url,
          discrepancy: false,
          discrepancyReason: null,
        });

        setMediaDiscrepancy(false);
        setMediaRemark("");
        setShowMediaRemark(false);

        setMediaDiscrepancies((previous) => ({
          ...previous,
          [mediaKey]: {
            discrepancy: false,
            remark: "",
          },
        }));
      } catch (error) {
        console.error("[MEDIA DISCREPANCY] Failed to update:", error);

        alert(
          error?.response?.data?.detail ||
            "Unable to update discrepancy. Please try again."
        );
      } finally {
        setMediaDiscrepancySaving(false);
      }

      return;
    }

    setMediaDiscrepancy(true);

    setMediaDiscrepancies((previous) => ({
      ...previous,
      [mediaKey]: {
        ...(previous[mediaKey] || {}),
        discrepancy: true,
      },
    }));

    setShowMediaRemark(true);
  };

  const handleSaveMediaRemark = async () => {
    const media = mediaItems[selectedMediaIndex];

    if (!media) return;

    const url = getMediaUrl(media);

    const jobId =
      mediaJob?.job_id || mediaJob?.request_id || mediaJob?.id;

    const sourceType = media.source_type || media.sourceType;
    const sourceId = media.source_id || media.sourceId;

    if (!jobId || !url || !sourceType || !sourceId) {
      alert("Cannot save remark: Missing media source information.");
      return;
    }

    const mediaKey = getMediaKey(media, selectedMediaIndex);

    const mediaIndex =
      media?.media_index ?? media?.mediaIndex ?? selectedMediaIndex;

    try {
      setMediaRemarkSaving(true);

      await updateMediaDiscrepancy({
        jobId,
        sourceType,
        sourceId,
        mediaIndex,
        mediaUrl: url,
        discrepancy: true,
        discrepancyReason: mediaRemark.trim() || null,
      });

      setMediaDiscrepancy(true);

      setMediaDiscrepancies((previous) => ({
        ...previous,
        [mediaKey]: {
          discrepancy: true,
          remark: mediaRemark.trim() || "",
        },
      }));

      setShowMediaRemark(true);
    } catch (error) {
      console.error("[MEDIA REMARK] Failed to save remark:", error);

      alert(
        error?.response?.data?.detail ||
          "Unable to save remark. Please try again."
      );
    } finally {
      setMediaRemarkSaving(false);
    }
  };

  const handleViewMedia = async (job) => {
    const requestId =
      job?.request_id || job?.job_id || job?.id;

    if (!requestId) {
      console.error("[JOB LIST MEDIA] Missing request_id:", job);
      return;
    }

    setMediaJob(job);
    setMediaItems([]);
    setSelectedMediaIndex(0);
    setMediaError("");

    setMediaDiscrepancy(null);
    setMediaDiscrepancies({});
    setMediaRemark("");
    setShowMediaRemark(false);

    setMediaOpen(true);
    setMediaLoading(true);
    setMediaImageLoading(true);

    try {
      const data = await fetchJobMedia(requestId);
      console.log("[JOB MEDIA FULL RESPONSE]", data);
      console.log("[JOB MEDIA MESSAGE JOB ID]", {
        message_job_id: data?.message_job_id,
        messageJobId: data?.messageJobId,
      });
      const images = Array.isArray(data?.images) ? data.images : [];
      const videos = Array.isArray(data?.videos) ? data.videos : [];
      console.log("[VIDEO SOURCE DEBUG]", videos);
      const media = [...images, ...videos];

      const enrichedMedia = media
        .map((item, index) => ({
          ...item,
          job_id: data?.job_id,
          source_type:
            item?.source_type || item?.sourceType || "job_message",
          source_id:
            item?.source_id ??
            item?.sourceId ??
            item?.message_id ??
            item?.messageId ??
            item?.id ??
            data?.message_job_id ??
            data?.messageJobId ??
            null,
          media_index:
            item?.media_index ?? item?.mediaIndex ?? index,
          type: item?.type || item?.media_type || "image",
        }))
        .filter((item) => getMediaUrl(item));

      setMediaItems(enrichedMedia);

      if (enrichedMedia.length === 0) {
        setMediaImageLoading(false);
      }
    } catch (err) {
      console.error("[JOB LIST MEDIA] API error:", err);

      setMediaError(
        err?.response?.data?.detail ||
          err?.message ||
          "Unable to load media."
      );

      setMediaImageLoading(false);
    } finally {
      setMediaLoading(false);
    }
  };

  const closeMedia = () => {
    setMediaOpen(false);
    setMediaJob(null);
    setMediaItems([]);
    setSelectedMediaIndex(0);
    setMediaError("");

    setMediaDiscrepancy(null);
    setMediaDiscrepancyLoading(false);
    setMediaDiscrepancySaving(false);

    setMediaDiscrepancies({});
    setMediaRemark("");
    setShowMediaRemark(false);
    setMediaRemarkSaving(false);

    setMediaImageLoading(false);
  };

  const changeMediaIndex = (nextIndex) => {
    const nextMedia = mediaItems[nextIndex];

    if (!nextMedia) return;

    const nextUrl = getMediaUrl(nextMedia);

    if (!nextUrl) return;

    setMediaError("");
    setMediaImageLoading(true);

    const isVideo = checkIsVideo(nextMedia);

    const onMediaSuccess = () => {
      setSelectedMediaIndex(nextIndex);

      const nextKey = getMediaKey(nextMedia, nextIndex);
      const nextState = mediaDiscrepancies[nextKey];

      setMediaDiscrepancy(nextState?.discrepancy ?? null);
      setMediaRemark(nextState?.remark ?? "");
      setShowMediaRemark(nextState?.discrepancy === true);

      setMediaImageLoading(false);
    };

    if (isVideo) {
      const videoElement = document.createElement("video");
      videoElement.onloadedmetadata = onMediaSuccess;
      videoElement.onerror = () => {
        setMediaImageLoading(false);
        setMediaError("Unable to load video.");
      };
      videoElement.src = nextUrl;
      videoElement.load();
    } else {
      const preloadImage = new Image();
      preloadImage.onload = onMediaSuccess;
      preloadImage.onerror = () => {
        setMediaImageLoading(false);
        setMediaError("Unable to load image.");
      };
      preloadImage.src = nextUrl;
    }
  };

  const showPreviousMedia = () => {
    if (mediaItems.length <= 1) return;

    const nextIndex =
      selectedMediaIndex > 0
        ? selectedMediaIndex - 1
        : mediaItems.length - 1;

    changeMediaIndex(nextIndex);
  };

  const showNextMedia = () => {
    if (mediaItems.length <= 1) return;

    const nextIndex =
      selectedMediaIndex < mediaItems.length - 1
        ? selectedMediaIndex + 1
        : 0;

    changeMediaIndex(nextIndex);
  };

  const handleThumbnailClick = (index) => {
    if (index === selectedMediaIndex) return;

    changeMediaIndex(index);
  };

  /*
  ========================================================
  PAGINATION DISPLAY
  ========================================================
  */

  const startRecord =
    total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;

  const endRecord = Math.min(page * PAGE_SIZE, total);

  /*
  ========================================================
  DATE FORMATTING HELPER
  ========================================================
  */

  const formatDateTime = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  /*
  ========================================================
  RENDER
  ========================================================
  */

  return (
    <div className="job-list-page">
      {/* HEADER */}
      <div className="job-list-header">
        <div>
          <h1>Job List</h1>
          <p>View jobs, campaigns and job status</p>
        </div>

        <button
          type="button"
          className="job-list-refresh"
          onClick={handleRefresh}
          disabled={loading}
        >
          <RefreshCw
            size={16}
            className={loading ? "job-list-refresh-spin" : ""}
          />
          Refresh
        </button>
      </div>

      {/* FILTERS */}
      <div className="job-list-filters">
        <div className="job-list-filter job-list-search-filter">
          <label>Search Job</label>
          <input
            type="text"
            value={jobSearch}
            onChange={(event) => setJobSearch(event.target.value)}
            placeholder="Search by job name..."
          />
        </div>

        <div className="job-list-filter">
          <label>Campaign</label>
          <select
            value={filters.campaign}
            onChange={(event) =>
              handleFilterChange("campaign", event.target.value)
            }
            disabled={filtersLoading}
          >
            <option value="">All Campaigns</option>
            {isInternalAdmin && (
              <option value="other">Other (No Campaign)</option>
            )}

            {campaigns.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.name}
              </option>
            ))}
          </select>
        </div>

        <div className="job-list-filter">
          <label>Brand</label>
          <select
            value={filters.brand}
            onChange={(event) =>
              handleFilterChange("brand", event.target.value)
            }
            disabled={filtersLoading}
          >
            <option value="">All Brands</option>
            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>
        </div>

        <div className="job-list-filter">
          <label>Status</label>
          <select
            value={filters.status}
            onChange={(event) =>
              handleFilterChange("status", event.target.value)
            }
          >
            <option value="">All Status</option>
            {statuses.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
            {/* Special date-based status */}
            <option value="today_completed">
              Today's Completed Jobs
            </option>
          </select>
        </div>
        <div className="job-list-filter">
          <label>Discrepancy</label>
          <select
            value={filters.discrepancy}
            onChange={(event) =>
              handleFilterChange("discrepancy", event.target.value)
            }
          >
            <option value="">All</option>
            <option value="yes">Yes</option>
          </select>
        </div>
        
        <button
          type="button"
          className="job-list-reset"
          onClick={handleReset}
          disabled={
            !jobSearch &&
            !filters.campaign &&
            !filters.brand &&
            !filters.status &&
            !filters.discrepancy
          }
        >
          Reset
        </button>
      </div>

      {/* ERROR */}
      {error && (
        <div className="job-list-error">
          <span>{error}</span>
          <button type="button" onClick={loadJobs}>
            Try Again
          </button>
        </div>
      )}
         <div className="job-list-pagination-info">
            Showing <strong>{startRecord}</strong> –{" "}
            <strong>{endRecord}</strong> of{" "}
            <strong>{total}</strong>
          </div>  
      {/* TABLE CARD */}
      <div className="job-list-card">
     
        <div className="job-list-table-wrapper">
          <table className="job-list-table">
            <thead>
              <tr>
                <th>Job Name</th>
                <th>Brand</th>
                <th>Campaign</th>
                <th>Status</th>
                <th>Discrepancy</th>
                <th>Created At</th>
                <th>Completed At</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan="8" className="job-list-loading">
                    <RefreshCw
                      size={26}
                      className="job-list-loading-spin"
                    />
                    <span>Loading jobs...</span>
                  </td>
                </tr>
              )}

              {!loading && jobs.length === 0 && (
                <tr>
                  <td colSpan="8" className="job-list-empty">
                    <Briefcase size={30} />
                    <strong>No jobs found</strong>
                    <span>Try changing the search or filters.</span>
                  </td>
                </tr>
              )}

              {!loading &&
                jobs.map((job, index) => (
                  <tr key={job.job_id || job.request_id || index}>
                    {/* JOB NAME & BUSINESS JOB ID */}
                    <td>
                      <div className="job-list-name-wrapper">

                        {/* JOB NAME */}
                        <div className="job-list-name">
                          {job.name || "—"}
                        </div>

                        {/* BUSINESS JOB ID */}
                        <div className="job-list-id">
                          Job ID: {job.snap_job_id || "—"}
                        </div>
                      </div>
                    </td>
                    <td>{job.brand || "—"}</td>
                    <td>{job.campaign || "—"}</td>
                    <td>
                      <span
                        className={`job-list-status status-${String(
                          job.status || ""
                        ).toLowerCase()}`}
                      >
                        {job.status || "—"}
                      </span>
                    </td>
                    <td>
                      {job.discrepancy === true ||
                      job.discrepancy === 1 ||
                      job.discrepancy === "1" ? (
                        <span className="job-list-discrepancy-value yes">
                          Yes
                        </span>
                      ) : (
                        <span className="job-list-discrepancy-value blank">
                          —
                        </span>
                      )}
                    </td>

                    <td>{formatDateTime(job.created_at || job.createdAt)}</td>
                    <td>{formatDateTime(job.completed_at || job.completedAt)}</td>
                    <td>
                      <div className="job-list-actions">
                        <button
                          type="button"
                          className="job-list-view"
                          onClick={() => handleViewJob(job)}
                        >
                          View
                          <ExternalLink size={14} />
                        </button>

                        <button
                          type="button"
                          className="job-list-media"
                          onClick={() => handleViewMedia(job)}
                        >
                          <Eye size={14} />
                          Media
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="job-list-pagination">
          <div className="job-list-pagination-info">
            Showing <strong>{startRecord}</strong> –{" "}
            <strong>{endRecord}</strong> of{" "}
            <strong>{total}</strong>
          </div>

          <div className="job-list-pagination-controls">
            <button
              type="button"
              disabled={loading || page <= 1}
              onClick={() => setPage((prev) => prev - 1)}
            >
              <ChevronLeft size={18} />
            </button>

            <span>
              Page <strong>{page}</strong>
              {totalPages > 0 && (
                <>
                  {" "}
                  of <strong>{totalPages}</strong>
                </>
              )}
            </span>

            <button
              type="button"
              disabled={
                loading || totalPages === 0 || page >= totalPages
              }
              onClick={() => setPage((prev) => prev + 1)}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* MEDIA MODAL */}
      {mediaOpen && (
        <div
          className="job-list-media-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Job media viewer"
          onClick={closeMedia}
        >
          <div
            className="job-list-media-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="job-list-media-modal-header">
              <div className="job-list-media-header-info">
                <h2>Job Media</h2>
                <span>
                  ({mediaJob?.name || `Job #${mediaJob?.snap_job_id || ""}`})
                </span>
              </div>

              <button
                type="button"
                className="job-list-media-close"
                onClick={closeMedia}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {mediaLoading && (
              <div className="job-list-media-state">
                <RefreshCw
                  size={28}
                  className="job-list-refresh-spin"
                />
                <span>Loading media...</span>
              </div>
            )}

            {!mediaLoading && mediaError && (
              <div className="job-list-media-state job-list-media-error">
                <strong>Unable to load media</strong>
                <span>{mediaError}</span>
              </div>
            )}

            {!mediaLoading &&
              !mediaError &&
              mediaItems.length === 0 && (
                <div className="job-list-media-state">
                  <Briefcase size={30} />
                  <strong>No media available</strong>
                  <span>
                    This job does not have any inspection media.
                  </span>
                </div>
              )}

            {!mediaLoading &&
              !mediaError &&
              mediaItems.length > 0 && (
                <div className="job-list-media-viewer">
                  <div className="job-list-media-main">
                    {mediaImageLoading && (
                      <div className="job-list-media-image-loading">
                        <RefreshCw
                          size={24}
                          className="job-list-refresh-spin"
                        />
                      </div>
                    )}

                    {mediaItems.length > 1 && (
                      <button
                        type="button"
                        className="job-list-media-nav previous"
                        onClick={showPreviousMedia}
                        aria-label="Previous media"
                      >
                        <ChevronLeft size={24} />
                      </button>
                    )}

                    {(() => {
                      const selectedMedia = mediaItems[selectedMediaIndex];
                      const mediaUrl = getMediaUrl(selectedMedia);
                      const isVideo = checkIsVideo(selectedMedia);

                      if (isVideo) {
                        return (
                          <video
                            src={mediaUrl}
                            controls
                            playsInline
                            preload="metadata"
                            className="media-preview-video job-list-media-image"
                            onLoadedData={() => setMediaImageLoading(false)}
                            onError={() => {
                              setMediaImageLoading(false);
                              setMediaError("Unable to load video.");
                            }}
                          />
                        );
                      }

                      return (
                        <img
                          src={mediaUrl}
                          alt={`Job media ${selectedMediaIndex + 1}`}
                          className="media-preview-image job-list-media-image"
                          onLoad={() => setMediaImageLoading(false)}
                          onError={() => {
                            setMediaImageLoading(false);
                            setMediaError("Unable to load image.");
                          }}
                        />
                      );
                    })()}

                    {mediaItems.length > 1 && (
                      <button
                        type="button"
                        className="job-list-media-nav next"
                        onClick={showNextMedia}
                        aria-label="Next media"
                      >
                        <ChevronRight size={24} />
                      </button>
                    )}
                  </div>

                  {/* MEDIA CONTROLS ROW */}
                  <div className="job-list-media-controls-row">
                    <div className="job-list-media-counter">
                      {selectedMediaIndex + 1} / {mediaItems.length}
                    </div>

                    {/* DISCREPANCY SECTION */}
                    <div className="job-list-discrepancy-section">
                      <span className="job-list-discrepancy-label">
                        Discrepancy found
                      </span>

                      <div className="job-list-discrepancy-toggle-wrapper">
                        <button
                          type="button"
                          className={`job-list-discrepancy-toggle ${
                            mediaDiscrepancy === true ? "active" : ""
                          }`}
                          onClick={() => handleDiscrepancyChange(!mediaDiscrepancy)}
                          disabled={
                            mediaDiscrepancyLoading || mediaDiscrepancySaving
                          }
                          aria-label="Toggle discrepancy"
                        >
                          <span className="job-list-discrepancy-toggle-track">
                            <span className="job-list-discrepancy-toggle-thumb" />
                          </span>
                        </button>
                      </div>

                      {(mediaDiscrepancyLoading || mediaDiscrepancySaving) && (
                        <span className="job-list-discrepancy-status">
                          <RefreshCw size={12} className="job-list-refresh-spin" />
                        </span>
                      )}
                    </div>
                  </div>

                  {/* REMARK INPUT AREA */}
                  {showMediaRemark &&
                    mediaDiscrepancy === true && (
                      <div className="job-list-media-remark">
                        <div className="job-list-media-remark-header">
                          <label htmlFor="media-discrepancy-remark">
                            Discrepancy Remark
                          </label>

                          <span>Optional</span>
                        </div>

                        <textarea
                          id="media-discrepancy-remark"
                          value={mediaRemark}
                          onChange={(event) =>
                            setMediaRemark(event.target.value)
                          }
                          placeholder="Enter the reason for the discrepancy..."
                          rows={2}
                          maxLength={500}
                        />

                        <div className="job-list-media-remark-footer">
                          <span>{mediaRemark.length}/500</span>

                          <button
                            type="button"
                            className="job-list-media-remark-save"
                            onClick={handleSaveMediaRemark}
                            disabled={mediaRemarkSaving}
                          >
                            {mediaRemarkSaving
                              ? "Saving..."
                              : "Save Remark"}
                          </button>
                        </div>
                      </div>
                    )}

                  {/* THUMBNAILS */}
                  <div className="job-list-media-thumbnails">
                    {mediaItems.map((media, index) => {
                      const url = getMediaUrl(media);

                      if (!url) return null;

                      const mediaKey = getMediaKey(media, index);

                      const hasDiscrepancy =
                        mediaDiscrepancies[mediaKey]?.discrepancy === true;

                      const isVideo = checkIsVideo(media);

                      return (
                        <button
                          type="button"
                          key={mediaKey}
                          className={`job-list-media-thumbnail ${
                            selectedMediaIndex === index
                              ? "active"
                              : ""
                          } ${
                            hasDiscrepancy
                              ? "has-discrepancy"
                              : ""
                          }`}
                          onClick={() => handleThumbnailClick(index)}
                        >
                          {isVideo ? (
                            <div className="media-video-thumbnail">
                              <video
                                src={url}
                                muted
                                preload="metadata"
                              />
                              <span className="video-play-icon">▶</span>
                            </div>
                          ) : (
                            <img
                              src={url}
                              alt={`Thumbnail ${index + 1}`}
                            />
                          )}

                          {hasDiscrepancy && (
                            <span
                              className="job-list-media-discrepancy-flag"
                              title="Discrepancy marked"
                            >
                              !
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}

export default JobList;